/* UnifiedDashboard — single dashboard layout shared by every COE staff_type.
 *
 * Driven by the unified `/api/coe/dashboard` endpoint. Each role gets:
 *   - SLA breach banner (only when count > 0)
 *   - "Action needed" grid of bucketed tiles (1-6 entries)
 *   - This-week stat strip (3 entries, tone-coded)
 *   - "Up next" list of 3 oldest open items
 *
 * The layout mirrors the planner dashboard's hierarchy so every COE member
 * gets the same polished, action-oriented landing — only the data shape
 * changes per role. Endpoint produces all role-shaped data server-side.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    ArrowRight, ChartLine, Clock, ShieldWarning, CheckCircle, ClipboardText,
    Flame, Phone, Warning, ShieldCheck, ChatCircleDots, Heart, ChatTeardropDots,
    ChatCircle, ArrowsClockwise, Lightning, Target, ClockUser,
} from "@phosphor-icons/react";
import CheckInNowModal from "@/components/coe/CheckInNowModal";
import { SentimentDot } from "@/components/coe/ComplianceSignals";
import PlannerInbox from "@/components/coe/PlannerInbox";
import { Briefcase, ChatText, Wrench, Timer, Tray, Star } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";


/* iter101.26 — Matches the Doctor Dashboard greeting exactly:
   "Wednesday, 4 Feb 2026 · Here's what's waiting on you today". */
function formatTodayLine() {
    return new Date().toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}


const TONES = {
    primary: { bg: "bg-primary", text: "text-white", soft: "bg-white/15", icon: "text-white", sub: "text-white/80" },
    amber:   { bg: "bg-amber-50",   text: "text-amber-900",   soft: "bg-amber-100",   icon: "text-amber-700",   sub: "text-amber-800/80" },
    rose:    { bg: "bg-rose-50",    text: "text-rose-900",    soft: "bg-rose-100",    icon: "text-rose-700",    sub: "text-rose-800/80" },
    violet:  { bg: "bg-violet-50",  text: "text-violet-900",  soft: "bg-violet-100",  icon: "text-violet-700",  sub: "text-violet-800/80" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-900", soft: "bg-emerald-100", icon: "text-emerald-700", sub: "text-emerald-800/80" },
    neutral: { bg: "bg-card", text: "text-foreground", soft: "bg-secondary/60", icon: "text-muted-foreground", sub: "text-muted-foreground" },
};

const STAT_TONES = {
    emerald: { border: "border-emerald-200", bg: "bg-emerald-50/50", icon: "text-emerald-700", num: "text-emerald-900" },
    amber:   { border: "border-amber-200",   bg: "bg-amber-50/50",   icon: "text-amber-700",   num: "text-amber-900" },
    rose:    { border: "border-rose-200",    bg: "bg-rose-50/50",    icon: "text-rose-700",    num: "text-rose-900" },
    violet:  { border: "border-violet-200",  bg: "bg-violet-50/50",  icon: "text-violet-700",  num: "text-violet-900" },
    primary: { border: "border-primary/20",  bg: "bg-primary/5",     icon: "text-primary",     num: "text-primary" },
};


function cleanFirst(raw) {
    const r = (raw || "").trim();
    const noPrefix = r.replace(/^(dr\.?|doctor)\s+/i, "");
    const noParen = noPrefix.replace(/\s*\([^)]*\)\s*$/, "").trim();
    return noParen.split(/\s+/)[0] || "there";
}


export default function UnifiedDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // iter98.99dw — Patient Engagement Specialist gets a richer 2-column
    // workspace (hot leads + compliance queue) instead of the generic
    // action-tiles + up-next layout. Routed in early so we skip the legacy
    // /coe/dashboard fetch when this role is logged in.
    const isPes = user?.staff_type === "counsellor";
    const [checkInCase, setCheckInCase] = useState(null);

    useEffect(() => {
        if (isPes) return;
        api.get("/coe/dashboard")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [isPes]);

    const greetName = cleanFirst(user?.name);

    if (isPes) {
        return (
            <>
                <PesHome firstName={greetName} userId={user?.id} onCheckIn={setCheckInCase} />
                <CheckInNowModal
                    open={!!checkInCase}
                    caseRow={checkInCase}
                    onClose={() => setCheckInCase(null)}
                    onSent={() => { /* PesHome refetches on remount; for now just close */ }}
                />
            </>
        );
    }

    const totalAction = data?.total_action_count ?? 0;
    // iter101.21 — Planner-only unified inbox at the very top. Solves the
    // "doctor responded but nothing showed up" complaint by folding every
    // "act NOW" signal (doctor_responded, pending_acceptance, plan_revision,
    // refinement, unread doctor chats) into one prominent list. Other COE
    // staff_types (production, tech, PES, QA, sales) don't need this because
    // their action tiles already surface their entire "waiting on me" set.
    const isPlanner = user?.staff_type === "planner" || user?.role === "planner";

    return (
        <div data-testid="unified-coe-dashboard" className="space-y-5 -mt-2">
            {/* HEADER — iter101.26 · matches Doctor Dashboard grammar exactly
                (text-2xl H1 + text-xs subtitle + rounded-full primary button).
                The COE role label and 3xl heading previously used here drifted
                away from the doctor portal aesthetic. User asked for pixel
                parity, so we adopt the exact same layout + typography. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">
                        Hello, {greetName} 👋
                    </h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {formatTodayLine()} · Here&apos;s what&apos;s waiting on you today.
                    </p>
                </div>
                {data?.primary_link && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            size="sm"
                            className="rounded-full"
                            onClick={() => navigate(data.primary_link.to)}
                            data-testid="dash-primary-link"
                        >
                            <Tray size={14} className="mr-1" weight="bold" /> {data.primary_link.label}
                        </Button>
                    </div>
                )}
            </div>

            {/* PLANNER INBOX — iter101.21 · Unified feed of everything
                that needs the planner's action RIGHT NOW. Injected only
                for planner staff so we don't confuse other COE roles
                that already have complete Action tiles below. */}
            {isPlanner && !loading && <PlannerInbox />}

            {/* iter101.27 — SLA breach banner removed. It was redundant now
                that (1) the PlannerInbox at the top surfaces every case
                waiting on the planner (breached items included), and (2)
                the new "SLA Watch" nested-stat card (Card 4 in Your Daily
                Focus below) shows Approaching + Breached counts with
                deep-links into the queue. Non-planner roles never had
                this banner rendered anyway (slaBreach = 0 for them). */}

            {/* iter101.27 — Planner "Your Daily Focus" section rebuilt to
                mirror the doctor portal's NestedStatCard grammar exactly
                (4 gradient cards · 2-3 nested metric rows each · icon-
                chip header). Per user direction each card groups related
                planner buckets so scanning the strip left→right walks the
                planner's own workflow: Intake → Awaiting response → QA/
                refinement → SLA watch. Other COE roles (production, QA,
                sales, tech, PES) keep the flat action-tile grid + stats
                strip because their workflow doesn't cleanly fold into
                the same 4-card story. */}
            {isPlanner ? (
                loading ? (
                    <SkeletonTileRow />
                ) : (
                    <>
                        <PlannerDailyFocus data={data} />
                        {/* iter101.28 — Throughput ribbon. Answers the planner
                            question "how many cases have I finalised?" — the
                            counter external planners need for billing and
                            internal planners want for performance review.
                            Sits below Daily Focus so it doesn't compete with
                            the actionable stack. */}
                        <PlannerThroughputStrip data={data} />
                    </>
                )
            ) : (
                <>
                    <section data-testid="dash-action-section">
                        <SectionHeader
                            title="Action needed"
                            subtitle={
                                loading
                                    ? "Loading…"
                                    : totalAction === 0
                                        ? "Nothing in your queue right now — well done."
                                        : `${totalAction} item${totalAction === 1 ? "" : "s"} waiting on you`
                            }
                        />
                        {loading ? (
                            <SkeletonTileRow />
                        ) : !data?.action_tiles?.length ? (
                            <EmptyTile message="No tiles configured for your role yet." />
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {data.action_tiles.map((t) => (
                                    <ActionTile key={t.key} tile={t} />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* PRODUCTIVITY / STATS STRIP */}
                    {data?.stats?.length > 0 && (
                        <section data-testid="dash-stats-section">
                            <SectionHeader title="This week" subtitle="Your throughput at a glance" />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {data.stats.slice(0, 3).map((s) => (
                                    <ProductivityStat key={s.key} stat={s} />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            {/* UP NEXT — iter101.56 · Hidden for planners per user
                direction. It duplicated My Queue's `Oldest first (SLA)`
                sort + priority stripes; planners drill into the queue
                for actual triage anyway. Other COE staff_types still
                see it (production, QA, tech, sales, admin). */}
            {!isPlanner && (
                <section data-testid="dash-upnext-section">
                    <SectionHeader
                        title="Up next"
                        subtitle="Your oldest active items — clear these first"
                        rightSlot={
                            data?.primary_link && (
                                <Link
                                    to={data.primary_link.to}
                                    className="text-xs text-primary font-semibold hover:underline whitespace-nowrap"
                                    data-testid="dash-upnext-see-all"
                                >
                                    See full queue <ArrowRight size={11} className="inline ml-0.5" />
                                </Link>
                            )
                        }
                    />
                    {loading ? (
                        <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>
                    ) : !data?.up_next?.length ? (
                        <div className="bg-card border border-dashed border-border rounded-2xl p-8 text-center text-xs text-muted-foreground" data-testid="dash-upnext-empty">
                            Nothing active yet — new items will appear here.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data.up_next.map((row) => <UpNextRow key={row.id} row={row} />)}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}


function UpNextRow({ row }) {
    const hrs = row.age_hours ?? 0;
    const ageTone = hrs > 48 ? "text-rose-700" : hrs > 24 ? "text-amber-700" : "text-muted-foreground";
    return (
        <Link
            to={row.link}
            className="flex items-center gap-3 bg-card border border-border rounded-xl p-3 hover:border-primary/40 hover:shadow-sm transition-all"
            data-testid={`dash-upnext-row-${row.id}`}
        >
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <ClipboardText size={18} weight="duotone" className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm truncate">{row.title}</span>
                    {row.badge && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                            {row.badge}
                        </span>
                    )}
                </div>
                {row.subtitle && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{row.subtitle}</div>}
            </div>
            <div className={`text-[11px] font-semibold tabular-nums shrink-0 ${ageTone}`}>{hrs}h</div>
            <ArrowRight size={14} className="text-muted-foreground shrink-0" />
        </Link>
    );
}


function SectionHeader({ title, subtitle, rightSlot }) {
    return (
        <div className="flex items-end justify-between mb-2.5 px-1">
            <div>
                <h2 className="font-display text-base font-semibold tracking-tight">{title}</h2>
                {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            {rightSlot}
        </div>
    );
}


function ActionTile({ tile }) {
    const t = TONES[tile.tone] || TONES.neutral;
    const zero = (tile.value ?? 0) === 0;
    const muted = zero ? "opacity-50" : "";
    return (
        <Link
            to={tile.link || "#"}
            data-testid={`dash-action-${tile.key}`}
            className={`group relative rounded-xl p-3 ${t.bg} ${t.text} ${muted} transition-all hover:-translate-y-0.5 hover:shadow-md border ${tile.tone === "primary" ? "border-transparent" : "border-border"}`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <div className={`w-7 h-7 rounded-lg ${t.soft} ${t.icon} flex items-center justify-center`}>
                    <ClipboardText size={14} weight="duotone" />
                </div>
                <ArrowRight size={12} className={`${t.sub} opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all`} />
            </div>
            <div className="flex items-baseline gap-2">
                <div className="font-display text-2xl font-bold tabular-nums leading-none">{tile.value ?? 0}</div>
                <div className="text-[12px] font-semibold leading-tight">{tile.label}</div>
            </div>
            {tile.sub && <div className={`text-[10px] mt-0.5 ${t.sub} truncate`}>{tile.sub}</div>}
        </Link>
    );
}


function ProductivityStat({ stat }) {
    const c = STAT_TONES[stat.tone] || STAT_TONES.primary;
    return (
        <div className={`rounded-xl p-3 border ${c.border} ${c.bg}`} data-testid={`dash-stat-${stat.key}`}>
            <div className="flex items-center gap-1.5">
                <ChartLine size={13} weight="fill" className={c.icon} />
                <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-foreground/70">{stat.label}</div>
            </div>
            <div className={`font-display text-xl font-bold tabular-nums mt-1 ${c.num}`}>{stat.value}</div>
            {stat.sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{stat.sub}</div>}
        </div>
    );
}


function SkeletonTileRow() {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-20 bg-card border border-border rounded-xl animate-pulse" />
            ))}
        </div>
    );
}


function EmptyTile({ message }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-8 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <CheckCircle size={22} weight="duotone" />
            </div>
            <div className="text-sm text-muted-foreground">{message}</div>
        </div>
    );
}


/* ─────────── Planner Daily Focus (iter101.27) ───────────
   4-card nested-metric snapshot mirroring the Doctor Dashboard's
   NestedStatCard grammar exactly. Cards group the planner's own
   workflow so scanning left→right walks the case lifecycle:
     Card 1 — Intake queue    (pending acceptance · in planning · plan revision)
     Card 2 — Awaiting reply  (ADR hold · with doctor · MFG spec pending)
     Card 3 — QA & refinement (QA bounced · in refinement · submitted to QA)
     Card 4 — SLA watch       (approaching SLA · breached)
   Per-user direction the section header ("Your Daily Focus") and
   sub-copy ("Four essential indicators for your day") match the
   doctor portal's snapshot strip word-for-word. */
function PlannerDailyFocus({ data }) {
    const tilesByKey = {};
    (data?.action_tiles || []).forEach((t) => { tilesByKey[t.key] = t; });
    const statsByKey = {};
    (data?.stats || []).forEach((s) => { statsByKey[s.key] = s; });
    const t = (key) => tilesByKey[key] || { value: 0, link: null };
    const s = (key) => statsByKey[key] || { value: 0 };

    // Emphasise the row's value in colour when non-zero and matters for
    // the planner's attention (rework, doctor waits, breaches).
    const emph = (val, tone) => (val > 0 ? tone : null);

    return (
        <section data-testid="planner-daily-focus">
            <SectionHeader
                title="Your Daily Focus"
                subtitle="Four essential indicators for your day"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="planner-focus-strip">
                {/* Card 1 — Intake queue */}
                <NestedStatCard
                    testid="planner-focus-intake"
                    label="Intake"
                    Icon={Briefcase}
                    iconTone="primary"
                    rows={[
                        {
                            testid: "planner-focus-pending-acceptance",
                            name: "Pending acceptance",
                            value: t("pending_acceptance").value,
                            sub: t("pending_acceptance").value > 0 ? "Admin routed to you" : "None waiting",
                            emphasise: emph(t("pending_acceptance").value, "amber"),
                            to: t("pending_acceptance").link,
                        },
                        {
                            testid: "planner-focus-in-planning",
                            name: "In planning",
                            value: t("in_planning").value,
                            sub: t("in_planning").value > 0 ? "Actively yours" : "Nothing in flight",
                            to: t("in_planning").link,
                        },
                        {
                            testid: "planner-focus-plan-revision",
                            name: "Plan revision",
                            value: t("plan_revision").value,
                            sub: t("plan_revision").value > 0 ? "Doctor asked for changes" : "No rework requests",
                            emphasise: emph(t("plan_revision").value, "amber"),
                            to: t("plan_revision").link,
                        },
                    ]}
                />

                {/* Card 2 — Awaiting response */}
                <NestedStatCard
                    testid="planner-focus-awaiting"
                    label="Awaiting response"
                    Icon={ChatText}
                    iconTone="amber"
                    rows={[
                        {
                            testid: "planner-focus-adr",
                            name: "ADR — hold",
                            value: t("adr").value,
                            sub: t("adr").value > 0 ? "Awaiting docs from doctor" : "No holds",
                            emphasise: emph(t("adr").value, "amber"),
                            to: t("adr").link,
                        },
                        {
                            testid: "planner-focus-with-doctor",
                            name: "With doctor",
                            value: t("with_doctor").value,
                            sub: t("with_doctor").value > 0 ? "Awaiting approval" : "Doctor is caught up",
                            to: t("with_doctor").link,
                        },
                        {
                            testid: "planner-focus-mfg-spec",
                            name: "MFG spec pending",
                            value: t("mfg_spec_pending").value,
                            sub: t("mfg_spec_pending").value > 0 ? "Doctor approved · finalise spec" : "No MFG specs owed",
                            emphasise: emph(t("mfg_spec_pending").value, "rose"),
                            to: t("mfg_spec_pending").link,
                        },
                    ]}
                />

                {/* Card 3 — QA & refinement */}
                <NestedStatCard
                    testid="planner-focus-qa"
                    label="QA & refinement"
                    Icon={Wrench}
                    iconTone="violet"
                    rows={[
                        {
                            testid: "planner-focus-qa-bounced",
                            name: "QA bounced",
                            value: t("qa_bounced").value,
                            sub: t("qa_bounced").value > 0 ? "Needs rework" : "Nothing bounced",
                            emphasise: emph(t("qa_bounced").value, "rose"),
                            to: t("qa_bounced").link,
                        },
                        {
                            testid: "planner-focus-refinement",
                            name: "In refinement",
                            value: t("refinement").value,
                            sub: t("refinement").value > 0 ? "Mid-treatment" : "None active",
                            to: t("refinement").link,
                        },
                        {
                            testid: "planner-focus-submitted-qa",
                            name: "Submitted to QA",
                            value: s("submitted_qa_7d").value,
                            sub: "Last 7 days",
                            to: "/coe/planner/queue?bucket=in_qa",
                        },
                    ]}
                />

                {/* Card 4 — SLA watch */}
                <NestedStatCard
                    testid="planner-focus-sla"
                    label="SLA watch"
                    Icon={Timer}
                    iconTone={s("breached_sla").value > 0 ? "rose" : (s("approaching_sla").value > 0 ? "amber" : "emerald")}
                    rows={[
                        {
                            testid: "planner-focus-approaching-sla",
                            name: "Approaching SLA",
                            value: s("approaching_sla").value,
                            sub: s("approaching_sla").value > 0 ? "24-48h with you" : "Comfortable window",
                            emphasise: emph(s("approaching_sla").value, "amber"),
                            to: "/coe/planner/queue?sla=approaching",
                        },
                        {
                            testid: "planner-focus-breached-sla",
                            name: "Breached",
                            value: s("breached_sla").value,
                            sub: s("breached_sla").value > 0 ? ">48h — needs immediate action" : "No breaches",
                            emphasise: emph(s("breached_sla").value, "rose"),
                            to: "/coe/planner/queue?sla=breached",
                        },
                    ]}
                />
            </div>
        </section>
    );
}


/* Planner throughput ribbon (iter101.28) — 2-metric horizontal strip
   showing "Planned this month" + "Planned all-time". Keeps the answer
   to "how am I doing?" one glance away without stealing the Daily
   Focus real-estate. Cards deep-link to the queue's new
   "Recently completed" bucket so the planner can jump straight to
   their finalised work list. */
function PlannerThroughputStrip({ data }) {
    const statsByKey = {};
    (data?.stats || []).forEach((s) => { statsByKey[s.key] = s; });
    const thisMonth = statsByKey.planned_this_month?.value ?? 0;
    const allTime = statsByKey.planned_all_time?.value ?? 0;
    return (
        <section data-testid="planner-throughput">
            <SectionHeader
                title="Your throughput"
                subtitle="Cases you have finalised · a running tally for productivity & billing"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                    to="/coe/planner/queue?bucket=planning_completed&sort=newest"
                    className="flex items-center gap-4 rounded-xl border border-primary/20 bg-gradient-to-br from-teal-50/80 via-teal-50/30 to-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
                    data-testid="planner-throughput-month"
                >
                    <span className="w-11 h-11 rounded-xl bg-primary/20 text-primary grid place-items-center shrink-0">
                        <ChartLine size={20} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            Planned this month
                        </div>
                        <div className="font-display text-2xl font-bold tabular-nums leading-none mt-1">
                            {thisMonth}
                        </div>
                        <div className="text-[10.5px] text-muted-foreground mt-1">
                            {thisMonth === 0 ? "Nothing finalised yet this month" : `Cases moved past MFG spec this month`}
                        </div>
                    </div>
                </Link>
                <Link
                    to="/coe/planner/queue?bucket=planning_completed&sort=newest"
                    className="flex items-center gap-4 rounded-xl border border-primary/20 bg-gradient-to-br from-emerald-50/80 via-emerald-50/30 to-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
                    data-testid="planner-throughput-alltime"
                >
                    <span className="w-11 h-11 rounded-xl bg-emerald-200/80 text-emerald-700 grid place-items-center shrink-0">
                        <CheckCircle size={20} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            Planned all-time
                        </div>
                        <div className="font-display text-2xl font-bold tabular-nums leading-none mt-1">
                            {allTime}
                        </div>
                        <div className="text-[10.5px] text-muted-foreground mt-1">
                            {allTime === 0 ? "Your first finalised case will show here" : `Lifetime cases finalised & handed to production`}
                        </div>
                    </div>
                </Link>
            </div>
        </section>
    );
}


/* Nested-metric card primitive — mirrors DoctorDashboard's NestedStatCard
   exactly (icon-chip header · gradient tint · divided metric rows). Rows
   are clickable Links so each row deep-links into the matching queue
   filter. `iconTone` drives both the icon-chip colour and the card's
   subtle gradient tint. */
function NestedStatCard({ testid, label, Icon, iconTone = "primary", rows }) {
    const TONE_BG = {
        primary: "bg-primary/20 text-primary",
        amber:   "bg-amber-200/80 text-amber-700",
        rose:    "bg-rose-200/80 text-rose-700",
        emerald: "bg-emerald-200/80 text-emerald-700",
        indigo:  "bg-indigo-200/80 text-indigo-700",
        violet:  "bg-violet-200/80 text-violet-700",
        neutral: "bg-secondary text-muted-foreground",
    };
    const TONE_CARD = {
        primary:  "border-teal-300 bg-gradient-to-br from-teal-100/70 via-teal-50/40 to-card",
        amber:    "border-amber-200 bg-gradient-to-br from-amber-100/60 via-amber-50/30 to-card",
        rose:     "border-rose-200 bg-gradient-to-br from-rose-100/60 via-rose-50/30 to-card",
        emerald:  "border-emerald-200 bg-gradient-to-br from-emerald-100/60 via-emerald-50/30 to-card",
        indigo:   "border-indigo-200 bg-gradient-to-br from-indigo-100/60 via-indigo-50/30 to-card",
        violet:   "border-violet-200 bg-gradient-to-br from-violet-100/60 via-violet-50/30 to-card",
        neutral:  "border-border bg-card",
    };
    const emphColor = {
        rose:  "text-rose-700",
        amber: "text-amber-700",
    };
    return (
        <div
            data-testid={testid}
            className={`border rounded-xl p-4 flex flex-col gap-2 hover:shadow-md hover:-translate-y-0.5 transition-all ${TONE_CARD[iconTone] || TONE_CARD.neutral}`}
        >
            <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    {label}
                </span>
                <span className={`w-7 h-7 rounded-lg ${TONE_BG[iconTone] || TONE_BG.neutral} grid place-items-center shrink-0`}>
                    <Icon size={14} weight="duotone" />
                </span>
            </div>
            <div className="divide-y divide-border/60 -mx-1">
                {rows.map((row) => {
                    const Wrapper = row.to ? Link : "div";
                    const wrapperProps = row.to ? { to: row.to } : {};
                    return (
                        <Wrapper
                            key={row.testid}
                            {...wrapperProps}
                            data-testid={row.testid}
                            className={`group flex items-center justify-between gap-2 px-1 py-2 rounded-md ${row.to ? "hover:bg-secondary/40" : ""} transition-colors`}
                        >
                            <div className="min-w-0">
                                <div className="text-[11px] text-muted-foreground truncate">{row.name}</div>
                                {row.sub && (
                                    <div className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{row.sub}</div>
                                )}
                            </div>
                            <div className={`font-display font-bold tabular-nums text-[18px] leading-none ${emphColor[row.emphasise] || "text-foreground"} ${row.to ? "group-hover:text-primary" : ""} transition-colors`}>
                                {row.value}
                            </div>
                        </Wrapper>
                    );
                })}
            </div>
        </div>
    );
}



/* ────────────────────────────────────────────────────────────────────
   iter98.99dw — PES Home (Phase A)

   Two-column workspace for the Patient Engagement Specialist role
   (staff_type='counsellor'). Pulls aggregated data from a single
   /api/coe/pes-home endpoint so the page renders in one round-trip.

   Layout:
     - Header (greeting + new responsibilities subtitle)
     - 5-tile quick stats bar (Hot leads · Check-in due · Overdue ·
       Escalations pending · Convos this week)
     - 2 panels side-by-side:
        · Hot leads (top 5 web prospects scoring ≥ 70)
        · Needs your call (top 5 compliance patients ranked by urgency)
   ──────────────────────────────────────────────────────────────────── */
function PesHome({ firstName, userId, onCheckIn }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // iter101.38 — Homepage availability surfaced on the dashboard header.
    // PES asked for one-tap control without a detour through My Profile.
    // Displays a live 3-state status (Offline / Online · on bench / Live
    // on homepage) — because multiple PES can be online at once but only
    // one is picked by the backend's `_pick_featured_counsellor` logic
    // (admin pin → sort_order → most recent updated_at). We fetch
    // /api/landing/card alongside the toggle state so we know whether
    // this user is currently the featured face on the public homepage.
    const [availability, setAvailability] = useState({ is_available_now: false, is_featured: false });
    const [featuredId, setFeaturedId] = useState(null);
    const [availLoading, setAvailLoading] = useState(true);
    const [availBusy, setAvailBusy] = useState(false);

    const loadAvailability = () => {
        Promise.all([
            api.get("/me/counsellor/availability"),
            api.get("/landing/card"),
        ])
            .then(([a, c]) => {
                setAvailability(a.data || { is_available_now: false, is_featured: false });
                setFeaturedId(c.data?.featured_counsellor?.id || null);
            })
            .catch(() => { /* fresh counsellors won't have a profile row yet — defaults hold */ })
            .finally(() => setAvailLoading(false));
    };

    const toggleAvailability = async (next) => {
        const prev = availability.is_available_now;
        setAvailability((a) => ({ ...a, is_available_now: next }));
        setAvailBusy(true);
        try {
            await api.patch("/me/counsellor/availability", { is_available_now: next });
            // Re-pull landing card so the "Live on homepage" badge flips
            // correctly (backend may have promoted the next counsellor on
            // the bench when we went offline, or vice-versa).
            const c = await api.get("/landing/card");
            const newFeaturedId = c.data?.featured_counsellor?.id || null;
            setFeaturedId(newFeaturedId);
            if (next) {
                toast.success(newFeaturedId === userId
                    ? "You're live on the homepage."
                    : "You're online — waiting on the bench.");
            } else {
                toast.success("You're offline — homepage will show the next available counsellor.");
            }
        } catch (e) {
            setAvailability((a) => ({ ...a, is_available_now: prev }));
            toast.error(e?.response?.data?.detail || "Couldn't update availability.");
        } finally { setAvailBusy(false); }
    };

    const refresh = () => {
        api.get("/coe/pes-home")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
        // Keep the availability + featured state fresh on manual refresh
        // too so the header pill can't drift out of sync with reality.
        loadAvailability();
    };

    useEffect(() => {
        refresh();
    }, []);

    const stats = data?.stats || {};
    const todayStats = data?.today_stats || {};
    const nba = data?.next_best_action || null;
    const urgentActions = data?.urgent_actions || [];
    const complianceQueue = data?.compliance_queue || [];
    // iter98.99ef — Doctor-resolved tasks ("typed TODOs") + the doctor-
    // replied unread badge banner that nudges Rachel to check the
    // escalations page.
    const pendingTasks = data?.pending_tasks || [];
    const doctorRepliesPending = stats.doctor_replies_pending_count || 0;

    // iter98.99ex — Redesigned action-ranked tiles (no vanity metrics).
    // Order: most urgent first → time-pressured → priority queues.
    // "Convos this week" and zero-valued "Tasks from providers" moved
    // off the strip (convos becomes a header subline; tasks render
    // only when count > 0).
    const TILES = [
        { key: "missed_callback", icon: Clock,       label: "Missed callback", value: stats.missed_callback_count, tone: "rose",    to: "/coe/leads" },
        { key: "overdue",         icon: Warning,     label: "Overdue check-in", value: stats.overdue_count,         tone: "rose",    to: "/coe/compliance" },
        { key: "sla_risk",        icon: ClockUser,   label: "SLA at risk",      value: stats.sla_risk_count,        tone: "amber",   to: "/coe/leads" },
        { key: "hot_untouched",   icon: Flame,       label: "Hot & untouched",  value: stats.hot_untouched_count,   tone: "amber",   to: "/coe/leads" },
        { key: "check_in",        icon: Phone,       label: "Check-in due",     value: stats.check_in_due_count,    tone: "primary", to: "/coe/compliance" },
        { key: "escalations",     icon: ShieldCheck, label: "Escalations pending", value: stats.open_escalations_count, tone: "violet", to: "/coe/escalations" },
    ];
    // iter98.99ef — Tasks from providers tile renders only when > 0
    // (otherwise it's noise — there's a dedicated panel below).
    if ((stats.pending_tasks_count || 0) > 0) {
        TILES.push({ key: "tasks", icon: ClipboardText, label: "Tasks from providers", value: stats.pending_tasks_count, tone: "emerald", to: "#tasks-from-doctors" });
    }

    return (
        <div data-testid="pes-home" className="space-y-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="overline text-primary mb-1.5">Patient Engagement</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                        Hi, {firstName}.
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
                        Web leads + post-fitment compliance — your two workstreams, one queue.
                    </p>
                    {/* iter101.40 — Removed the "TODAY · 0 calls · 0 check-ins ·
                        0 escalations resolved" strip that used to sit here.
                        It's now redundant with the "Touches today" row
                        inside the Provider tasks card in Daily Focus (which
                        rolls up the same three counters). */}
                </div>
                {/* iter101.38 — Availability pill + Refresh button live
                    side-by-side. Pill shows a live 3-state summary of
                    where this PES sits with respect to the public homepage
                    (Offline / Online · on bench / Live on homepage) and
                    lets them flip the switch inline — no detour through
                    My Profile. */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <PesAvailabilityPill
                        availability={availability}
                        featuredId={featuredId}
                        userId={userId}
                        loading={availLoading}
                        busy={availBusy}
                        onToggle={toggleAvailability}
                    />
                    {/* iter98.99ew — Manual refresh button (parity with Inbox /
                        My Queue / Compliance). PES staff want a way to re-pull
                        dashboard counts without a full page reload. */}
                    <button
                        type="button"
                        onClick={refresh}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-sm hover:bg-secondary/40 transition"
                        data-testid="pes-home-refresh"
                    >
                        <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            {/* iter98.99ef — Doctor-replied alert banner. Only renders when
                there's at least one unread doctor comment on an escalation
                Rachel raised. Clicking opens the Escalations page (which
                auto-resets coach_unread_count to 0, so the banner goes
                away on next dashboard load). */}
            {!loading && doctorRepliesPending > 0 && (
                <button
                    type="button"
                    onClick={() => navigate("/coe/escalations")}
                    className="w-full rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 via-rose-100/70 to-rose-50 px-5 py-3 flex items-center gap-4 text-left hover:shadow-md transition-shadow"
                    data-testid="pes-doctor-replied-banner"
                >
                    <div className="w-10 h-10 rounded-full bg-rose-600 grid place-items-center text-white shrink-0">
                        <ChatTeardropDots size={18} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-rose-800">Provider replied</div>
                        <div className="text-sm font-semibold text-rose-900 mt-0.5">
                            {doctorRepliesPending === 1
                                ? "1 escalation has a new reply from the provider."
                                : `${doctorRepliesPending} escalations have new replies from providers.`}
                        </div>
                        <div className="text-[11.5px] text-rose-800/80 mt-0.5">Open the Escalations page to read and respond.</div>
                    </div>
                    <ArrowRight size={16} weight="bold" className="text-rose-700 shrink-0" />
                </button>
            )}

            {/* iter98.99ex — Next Best Action hero card. The single most
                urgent item across web leads + compliance, with a one-click
                primary CTA so the PES never has to scan tiles to figure
                out where to start. Hidden when nothing is urgent. */}
            {!loading && nba && (
                <NextBestActionCard nba={nba} onOpen={() => navigate(nba.link)} />
            )}

            {/* iter98.99ex — Action-ranked stat tiles (no vanity metrics).
                iter101.37 — Consolidated the flat 6-tile strip into 4
                grouped NestedStatCards under a "Your Daily Focus" header
                so PES visually rhymes with the treatment-planner portal.
                Same underlying data; grouping by workstream (Web leads /
                Compliance / Escalations / Provider tasks) makes it feel
                organised rather than a random pile of numbers. */}
            <PesDailyFocus stats={stats} todayStats={todayStats} />

            {/* 2-column workspace */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PesPanel
                    testid="pes-urgent-actions-panel"
                    iconColor="text-rose-600"
                    Icon={Lightning}
                    title="Urgent actions"
                    subtitle="What needs your attention right now"
                    seeAllTo="/coe/leads"
                    emptyText="You're all caught up — no overdue callbacks, no breached SLAs, no untouched hot leads."
                    loading={loading}
                    items={urgentActions}
                    renderItem={(a) => <PesUrgentActionRow action={a} key={a.id} />}
                />
                <PesPanel
                    testid="pes-compliance-panel"
                    iconColor="text-primary"
                    Icon={Heart}
                    title="Needs your call"
                    subtitle="Compliance patients with the highest signal"
                    seeAllTo="/coe/compliance"
                    emptyText="All caught up on compliance. Once admin assigns patients to you, they'll show up here."
                    loading={loading}
                    items={complianceQueue}
                    renderItem={(c) => <PesComplianceRow row={c} key={c.case_id} onCheckIn={onCheckIn} />}
                />
            </div>

            {/* iter101.37 — Throughput strip. Mirrors the planner portal's
                two-card grammar (weekly + all-time) but with PES-native
                metrics: conversations this week + total leads converted. */}
            <PesThroughputStrip stats={stats} todayStats={todayStats} />

            {/* iter98.99ef — Tasks from doctors. Typed TODOs the doctor
                created when resolving an escalation with an action chip.
                Only renders the section when there's something pending so
                empty-state space isn't wasted. */}
            <div id="tasks-from-doctors">
                <PesTasksPanel
                    loading={loading}
                    tasks={pendingTasks}
                    totalCount={stats.pending_tasks_count || 0}
                    onCompleted={refresh}
                />
            </div>
        </div>
    );
}

/* iter101.38 — Compact availability pill for the PES dashboard header.
   Renders a live 3-state summary + a Switch that flips
   /api/me/counsellor/availability. The three states are:

     • Offline           — is_available_now=false. Grey dot. Homepage
                           will not show this counsellor at all.
     • Online · on bench — is_available_now=true but the backend's
                           `_pick_featured_counsellor` picked someone
                           else (multiple PES online at once — admin
                           pin → sort_order → most recent updated_at
                           breaks the tie). Emerald dot.
     • Live on homepage  — is_available_now=true AND the landing card
                           currently shows this counsellor. Amber star.

   Backend truth is read from /api/landing/card's featured_counsellor.id
   compared to the current user's id (passed in via prop). */
function PesAvailabilityPill({ availability, featuredId, userId, loading, busy, onToggle }) {
    const isOnline = !!availability?.is_available_now;
    const isLive = isOnline && featuredId && featuredId === userId;
    const status = isLive ? "live" : (isOnline ? "online" : "offline");

    const tone = {
        live:    { dot: "bg-amber-500",   ring: "ring-amber-200",   text: "text-amber-800",     sub: "text-amber-700/80",    border: "border-amber-200",   bg: "bg-amber-50/60"   },
        online:  { dot: "bg-emerald-500", ring: "ring-emerald-200", text: "text-emerald-800",   sub: "text-emerald-700/80",  border: "border-emerald-200", bg: "bg-emerald-50/60" },
        offline: { dot: "bg-muted-foreground/50", ring: "ring-muted-foreground/10", text: "text-muted-foreground", sub: "text-muted-foreground/70", border: "border-border", bg: "bg-card" },
    }[status];

    const label = isLive ? "Live on homepage" : isOnline ? "Online · on bench" : "Offline";
    const help = isLive
        ? "Public homepage shows you right now"
        : isOnline
            ? "Ready · another PES is currently featured"
            : "Hidden from public homepage";

    return (
        <div
            data-testid="pes-home-availability"
            data-status={status}
            className={`inline-flex items-center gap-2.5 pl-2.5 pr-2 py-1.5 rounded-lg border ${tone.border} ${tone.bg} transition-colors`}
        >
            <span className="relative flex items-center justify-center shrink-0">
                {isLive ? (
                    <Star size={12} weight="fill" className="text-amber-500" data-testid="pes-home-availability-star" />
                ) : (
                    <span className={`w-2 h-2 rounded-full ${tone.dot} ring-4 ${tone.ring} transition-colors`} />
                )}
            </span>
            <div className="flex flex-col leading-tight min-w-0">
                <span className={`text-[11px] font-semibold whitespace-nowrap ${tone.text}`} data-testid="pes-home-availability-status">
                    {label}
                </span>
                <span className={`text-[9.5px] whitespace-nowrap ${tone.sub}`}>{help}</span>
            </div>
            <Switch
                checked={isOnline}
                onCheckedChange={onToggle}
                disabled={loading || busy}
                data-testid="pes-home-availability-toggle"
                aria-label="Toggle homepage availability"
                className="ml-1 shrink-0"
            />
        </div>
    );
}



/* iter101.37 — PES Daily Focus. Four grouped NestedStatCards under a
   SectionHeader; matches the treatment-planner portal's Daily Focus
   grammar 1:1 so the two dashboards feel like siblings. */
function PesDailyFocus({ stats, todayStats }) {
    const emph = (val, tone) => (val > 0 ? tone : null);
    return (
        <section data-testid="pes-daily-focus">
            <SectionHeader
                title="Your Daily Focus"
                subtitle="Four essential workstreams for your day"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pes-focus-strip">
                {/* Card 1 — Web leads (proactive lane) */}
                <NestedStatCard
                    testid="pes-focus-web-leads"
                    label="Web leads"
                    Icon={Flame}
                    iconTone="rose"
                    rows={[
                        {
                            testid: "pes-focus-missed-callback",
                            name: "Missed callback",
                            value: stats.missed_callback_count || 0,
                            sub: (stats.missed_callback_count || 0) > 0 ? "Follow up now" : "None waiting",
                            emphasise: emph(stats.missed_callback_count, "rose"),
                            to: "/coe/leads?filter=missed_callback",
                        },
                        {
                            testid: "pes-focus-hot-untouched",
                            name: "Hot & untouched",
                            value: stats.hot_untouched_count || 0,
                            sub: (stats.hot_untouched_count || 0) > 0 ? "Score ≥ 70, no reply yet" : "All hot leads touched",
                            emphasise: emph(stats.hot_untouched_count, "amber"),
                            to: "/coe/leads?filter=hot_untouched",
                        },
                        {
                            testid: "pes-focus-sla-risk",
                            name: "SLA at risk",
                            value: stats.sla_risk_count || 0,
                            sub: (stats.sla_risk_count || 0) > 0 ? "Within 24h of breach" : "No breaches looming",
                            emphasise: emph(stats.sla_risk_count, "amber"),
                            to: "/coe/leads?filter=sla_risk",
                        },
                    ]}
                />

                {/* Card 2 — Compliance */}
                <NestedStatCard
                    testid="pes-focus-compliance"
                    label="Compliance"
                    Icon={Heart}
                    iconTone="primary"
                    rows={[
                        {
                            testid: "pes-focus-overdue",
                            name: "Overdue check-in",
                            value: stats.overdue_count || 0,
                            sub: (stats.overdue_count || 0) > 0 ? "SLA breached" : "All check-ins on time",
                            emphasise: emph(stats.overdue_count, "rose"),
                            to: "/coe/compliance?filter=overdue",
                        },
                        {
                            testid: "pes-focus-check-in-due",
                            name: "Check-in due",
                            value: stats.check_in_due_count || 0,
                            sub: (stats.check_in_due_count || 0) > 0 ? "Due today" : "Nothing due today",
                            to: "/coe/compliance?filter=due",
                        },
                        {
                            testid: "pes-focus-awaiting-reply",
                            name: "Awaiting reply",
                            value: stats.awaiting_reply_count || 0,
                            sub: (stats.awaiting_reply_count || 0) > 0 ? "Patient hasn't replied" : "No patients silent",
                            emphasise: emph(stats.awaiting_reply_count, "amber"),
                            to: "/coe/compliance?filter=awaiting_reply",
                        },
                    ]}
                />

                {/* Card 3 — Escalations */}
                <NestedStatCard
                    testid="pes-focus-escalations"
                    label="Escalations"
                    Icon={ShieldCheck}
                    iconTone="violet"
                    rows={[
                        {
                            testid: "pes-focus-esc-open",
                            name: "Open",
                            value: stats.open_escalations_count || 0,
                            sub: (stats.open_escalations_count || 0) > 0 ? "You raised these" : "No open escalations",
                            emphasise: emph(stats.open_escalations_count, "amber"),
                            to: "/coe/escalations",
                        },
                        {
                            testid: "pes-focus-esc-replied",
                            name: "Provider replied",
                            value: stats.doctor_replies_pending_count || 0,
                            sub: (stats.doctor_replies_pending_count || 0) > 0 ? "New response waiting" : "No new replies",
                            emphasise: emph(stats.doctor_replies_pending_count, "rose"),
                            to: "/coe/escalations",
                        },
                        {
                            testid: "pes-focus-esc-resolved-today",
                            name: "Resolved today",
                            value: todayStats.escalations_resolved || 0,
                            sub: (todayStats.escalations_resolved || 0) > 0 ? "Since midnight" : "Room to close some out",
                            to: "/coe/escalations?filter=resolved_today",
                        },
                    ]}
                />

                {/* Card 4 — Provider tasks */}
                <NestedStatCard
                    testid="pes-focus-provider-tasks"
                    label="Provider tasks"
                    Icon={ClipboardText}
                    iconTone="amber"
                    rows={[
                        {
                            testid: "pes-focus-tasks-pending",
                            name: "Pending",
                            value: stats.pending_tasks_count || 0,
                            sub: (stats.pending_tasks_count || 0) > 0 ? "Typed TODOs from doctors" : "No open tasks",
                            emphasise: emph(stats.pending_tasks_count, "amber"),
                            to: "#tasks-from-doctors",
                        },
                        {
                            testid: "pes-focus-convos-week",
                            name: "Convos this week",
                            value: stats.this_week_conversations || 0,
                            sub: (stats.this_week_conversations || 0) > 0 ? "Patients you touched" : "Time to reach out",
                            to: "/coe/compliance",
                        },
                        {
                            testid: "pes-focus-touches-today",
                            name: "Touches today",
                            value: (todayStats.calls || 0) + (todayStats.check_ins || 0) + (todayStats.escalations_resolved || 0),
                            sub: "Calls + check-ins + resolves",
                            to: null,
                        },
                    ]}
                />
            </div>
        </section>
    );
}

/* iter101.37 — PES Throughput strip. Mirrors PlannerThroughputStrip 1:1
   (2 gradient cards side-by-side) but with PES-native metrics: this
   week's conversation volume + all-time conversions. */
function PesThroughputStrip({ stats, todayStats }) {
    const thisWeek = stats.this_week_conversations || 0;
    const todayTouches = (todayStats.calls || 0) + (todayStats.check_ins || 0) + (todayStats.escalations_resolved || 0);
    return (
        <section data-testid="pes-throughput">
            <SectionHeader
                title="Your throughput"
                subtitle="Conversations you've moved forward · a running tally for productivity"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                    to="/coe/compliance"
                    className="flex items-center gap-4 rounded-xl border border-primary/20 bg-gradient-to-br from-teal-50/80 via-teal-50/30 to-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
                    data-testid="pes-throughput-week"
                >
                    <span className="w-11 h-11 rounded-xl bg-primary/20 text-primary grid place-items-center shrink-0">
                        <ChatCircleDots size={20} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            Conversations this week
                        </div>
                        <div className="font-display text-2xl font-bold tabular-nums leading-none mt-1">
                            {thisWeek}
                        </div>
                        <div className="text-[10.5px] text-muted-foreground mt-1">
                            {thisWeek === 0 ? "No conversations yet this week" : "Patients + leads you've moved forward"}
                        </div>
                    </div>
                </Link>
                <div
                    className="flex items-center gap-4 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/80 via-emerald-50/30 to-card p-4"
                    data-testid="pes-throughput-today"
                >
                    <span className="w-11 h-11 rounded-xl bg-emerald-200/80 text-emerald-700 grid place-items-center shrink-0">
                        <CheckCircle size={20} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            Touches today
                        </div>
                        <div className="font-display text-2xl font-bold tabular-nums leading-none mt-1">
                            {todayTouches}
                        </div>
                        <div className="text-[10.5px] text-muted-foreground mt-1">
                            {todayTouches === 0 ? "No touches logged yet today" : "Calls + check-ins + escalations closed"}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


/* iter98.99ef — Tasks from doctors panel. Each row shows the kind icon,
   the task label, patient + case context, and a "Mark done" button. The
   row is clickable to open the source escalation (where the doctor's
   note + the full thread live). */
const TASK_KIND_META = {
    custom:             { Icon: ChatCircle, tone: "neutral", verb: "Follow up" },
    continue_current:   { Icon: CheckCircle, tone: "emerald", verb: "Reinforce" },
    pause_wear:         { Icon: Clock, tone: "amber", verb: "Confirm pause" },
    book_visit:         { Icon: ChatTeardropDots, tone: "primary", verb: "Book visit" },
    order_replacement:  { Icon: ArrowsClockwise, tone: "violet", verb: "Replacement" },
    send_to_refinement: { Icon: ShieldWarning, tone: "rose", verb: "Refinement" },
};

const TASK_TONE_CLS = {
    emerald: "bg-emerald-100 text-emerald-800",
    amber:   "bg-amber-100 text-amber-800",
    primary: "bg-primary/10 text-primary",
    violet:  "bg-violet-100 text-violet-800",
    rose:    "bg-rose-100 text-rose-800",
    neutral: "bg-secondary text-foreground",
};

function PesTasksPanel({ loading, tasks, totalCount, onCompleted }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="pes-tasks-panel">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                    <ClipboardText size={20} weight="duotone" className="text-emerald-700 shrink-0" />
                    <div className="min-w-0">
                        <h3 className="font-display text-base font-bold leading-tight">Tasks from providers</h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            Typed TODOs created when a provider resolves one of your escalations
                        </p>
                    </div>
                </div>
                {totalCount > 5 && (
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0" data-testid="pes-tasks-more-count">
                        +{totalCount - 5} more
                    </span>
                )}
            </div>
            {loading ? (
                <div className="space-y-2">
                    {[1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/50 animate-pulse" />)}
                </div>
            ) : tasks.length === 0 ? (
                <div className="text-center py-8" data-testid="pes-tasks-empty">
                    <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-2">
                        <CheckCircle size={20} className="text-emerald-600" weight="duotone" />
                    </div>
                    <div className="text-sm font-medium text-muted-foreground">No pending tasks.</div>
                    <p className="text-[11.5px] text-muted-foreground/80 mt-1 max-w-md mx-auto">
                        When a provider resolves your escalation with an outcome chip, you&apos;ll get a clear TODO here.
                    </p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="pes-tasks-list">
                    {tasks.map((t) => <PesTaskRow task={t} key={t.id} onCompleted={onCompleted} />)}
                </ul>
            )}
        </div>
    );
}

function PesTaskRow({ task, onCompleted }) {
    const navigate = useNavigate();
    const [completing, setCompleting] = useState(false);
    const meta = TASK_KIND_META[task.kind] || TASK_KIND_META.custom;
    const Icon = meta.Icon;
    const toneCls = TASK_TONE_CLS[meta.tone] || TASK_TONE_CLS.neutral;

    const openSource = () => {
        // Deep-link to Escalations — the doctor's resolution note + the
        // full conversation live there, which is the context the coach
        // needs to action the TODO.
        navigate("/coe/escalations");
    };

    const markDone = async (e) => {
        e.stopPropagation();
        setCompleting(true);
        try {
            await api.post(`/coe/pes-tasks/${task.id}/complete`);
            toast.success("Task marked done");
            onCompleted?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't mark done");
            setCompleting(false);
        }
    };

    return (
        <li
            onClick={openSource}
            className="flex items-center gap-3 p-3 border border-border rounded-xl hover:bg-secondary/40 transition-colors cursor-pointer group"
            data-testid={`pes-task-row-${task.id}`}
        >
            <span className={`shrink-0 w-8 h-8 rounded-full grid place-items-center ${toneCls}`}>
                <Icon size={14} weight="fill" />
            </span>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm leading-tight truncate" data-testid={`pes-task-label-${task.id}`}>
                    {task.label}
                </div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5 flex items-center gap-1.5">
                    {task.patient_name || "Patient"}
                    {task.case_code && <><span>·</span><span className="font-mono">{task.case_code}</span></>}
                    <span>·</span>
                    <span className="uppercase tracking-wider text-[9.5px] font-bold opacity-70">{meta.verb}</span>
                </div>
            </div>
            <button
                type="button"
                onClick={markDone}
                disabled={completing}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-[11px] font-semibold transition opacity-80 group-hover:opacity-100"
                data-testid={`pes-task-done-${task.id}`}
                title="Mark this task done"
            >
                <CheckCircle size={11} weight="fill" />
                {completing ? "…" : "Done"}
            </button>
        </li>
    );
}

// iter98.99ex — Next Best Action hero card. Single highest-priority
// item across web leads + compliance, with a one-click CTA. Reduces
// decision fatigue: PES doesn't have to scan tiles to know where to start.
function NextBestActionCard({ nba, onOpen }) {
    const toneCls = {
        rose:    { bg: "from-rose-50 via-rose-100/70 to-rose-50", border: "border-rose-200", icon: "bg-rose-600", text: "text-rose-900", sub: "text-rose-800/80", over: "text-rose-800" },
        amber:   { bg: "from-amber-50 via-amber-100/70 to-amber-50", border: "border-amber-200", icon: "bg-amber-600", text: "text-amber-900", sub: "text-amber-800/80", over: "text-amber-800" },
        primary: { bg: "from-primary/5 via-primary/10 to-primary/5", border: "border-primary/20", icon: "bg-primary", text: "text-foreground", sub: "text-muted-foreground", over: "text-primary" },
        violet:  { bg: "from-violet-50 via-violet-100/70 to-violet-50", border: "border-violet-200", icon: "bg-violet-600", text: "text-violet-900", sub: "text-violet-800/80", over: "text-violet-800" },
    }[nba.tone] || { bg: "from-primary/5 to-primary/5", border: "border-primary/20", icon: "bg-primary", text: "text-foreground", sub: "text-muted-foreground", over: "text-primary" };
    const HeroIcon = nba.kind === "compliance" ? Heart : Target;
    return (
        <div
            className={`rounded-2xl border ${toneCls.border} bg-gradient-to-r ${toneCls.bg} px-5 py-4 flex items-start gap-4`}
            data-testid="pes-next-best-action"
        >
            <div className={`w-12 h-12 rounded-2xl ${toneCls.icon} grid place-items-center text-white shrink-0`}>
                <HeroIcon size={22} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
                <div className={`text-[10px] uppercase tracking-[0.16em] font-bold ${toneCls.over}`}>
                    Next best action
                </div>
                <div className={`font-display text-lg font-bold ${toneCls.text} truncate`} data-testid="pes-nba-name">
                    {nba.name}
                    {nba.score != null && nba.score > 0 && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-white/60 text-foreground/70 px-1.5 py-0.5 rounded">
                            Score {nba.score}
                        </span>
                    )}
                </div>
                <div className={`text-[12.5px] ${toneCls.sub} mt-0.5 truncate`}>
                    {nba.subtitle}
                </div>
                {nba.preferred_slot && (
                    <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${toneCls.over}`}>
                        <Clock size={11} weight="fill" /> Caller asked for {nba.preferred_slot}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {nba.phone && nba.kind === "lead" && (
                    <a
                        href={`tel:${nba.phone}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                        data-testid="pes-nba-call-btn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Phone size={14} weight="fill" /> Call now
                    </a>
                )}
                <button
                    type="button"
                    onClick={onOpen}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-card border border-border hover:bg-secondary/40 text-sm font-semibold transition-colors"
                    data-testid="pes-nba-open-btn"
                >
                    Open <ArrowRight size={13} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function PesStatTile({ icon: Icon, label, value, tone, to, loading }) {
    const toneCls = {
        rose: "bg-rose-50 text-rose-700 border-rose-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        primary: "bg-primary/5 text-primary border-primary/20",
        violet: "bg-violet-50 text-violet-700 border-violet-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    }[tone] || "bg-secondary border-border";
    const display = loading ? "…" : (value ?? 0);
    const inner = (
        <div
            className={`px-3 py-3 rounded-2xl border ${toneCls} flex items-center gap-2.5 transition-shadow h-full ${to ? "hover:shadow-md" : ""}`}
            data-testid={`pes-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <Icon size={18} weight="duotone" className="shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="font-display text-2xl font-bold tabular-nums leading-none">{display}</div>
                {/* iter98.99ew — Allow label to wrap to 2 lines so
                    "Tasks from providers", "Convos this week", and
                    "Check-in due" don't truncate with an ellipsis on
                    narrow cards. tight leading + 2-line clamp keeps the
                    card height consistent. */}
                <div
                    className="text-[10px] uppercase tracking-[0.1em] font-semibold mt-1.5 leading-tight"
                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                    {label}
                </div>
            </div>
        </div>
    );
    return to ? <Link to={to}>{inner}</Link> : inner;
}

function PesPanel({ testid, iconColor, Icon, title, subtitle, seeAllTo, emptyText, loading, items, renderItem }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid={testid}>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon size={20} weight="duotone" className={`${iconColor} shrink-0`} />
                    <div className="min-w-0">
                        <h3 className="font-display text-base font-bold leading-tight">{title}</h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
                    </div>
                </div>
                <Link
                    to={seeAllTo}
                    className="shrink-0 text-[11px] text-primary font-semibold hover:underline inline-flex items-center gap-1"
                    data-testid={`${testid}-see-all`}
                >
                    See all <ArrowRight size={11} weight="bold" />
                </Link>
            </div>
            {loading ? (
                <div className="space-y-2">
                    {[1, 2, 3].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/50 animate-pulse" />)}
                </div>
            ) : items.length === 0 ? (
                <p className="text-xs text-muted-foreground py-6 text-center" data-testid={`${testid}-empty`}>{emptyText}</p>
            ) : (
                <div className="space-y-2">{items.map(renderItem)}</div>
            )}
        </div>
    );
}

function PesUrgentActionRow({ action }) {
    // iter98.99em — Reason chip drives the row's tone. Rank 4 (overdue
    // callback) and 3 (breached SLA) get rose; rank 2 (hot+untouched)
    // gets amber; rank 1 (awaiting reply) gets primary teal.
    const toneCls = {
        rose:    { dot: "bg-rose-500",    chip: "bg-rose-100 text-rose-700",       label: "text-rose-700" },
        amber:   { dot: "bg-amber-500",   chip: "bg-amber-100 text-amber-800",     label: "text-amber-700" },
        primary: { dot: "bg-primary",     chip: "bg-primary/10 text-primary",      label: "text-primary"   },
    }[action.tone] || { dot: "bg-secondary", chip: "bg-secondary text-foreground", label: "text-foreground" };
    return (
        <Link
            to={action.link}
            className="flex items-center gap-3 p-3 border border-border rounded-xl hover:bg-secondary/40 transition-colors group"
            data-testid={`pes-urgent-action-${action.id}`}
        >
            <span className={`w-1.5 h-10 rounded-full shrink-0 ${toneCls.dot}`} aria-hidden />
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" data-testid={`pes-urgent-name-${action.id}`}>{action.name}</div>
                <div className={`text-[11px] mt-0.5 truncate font-medium ${toneCls.label}`} data-testid={`pes-urgent-reason-${action.id}`}>
                    {action.reason_label}
                </div>
            </div>
            {action.score >= 70 && (
                <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-bold inline-flex items-center gap-1 shrink-0 ${toneCls.chip}`}>
                    <Flame size={10} weight="fill" /> {action.score}
                </span>
            )}
            <ArrowRight size={13} weight="bold" className="text-muted-foreground group-hover:translate-x-0.5 group-hover:text-foreground transition-all shrink-0" />
        </Link>
    );
}

function PesComplianceRow({ row, onCheckIn }) {
    const toneCls = {
        rose: "bg-rose-100 text-rose-700",
        amber: "bg-amber-100 text-amber-800",
        primary: "bg-primary/10 text-primary",
        emerald: "bg-emerald-100 text-emerald-700",
    }[row.tone] || "bg-secondary text-muted-foreground";
    return (
        <div className="flex items-center gap-3 p-3 border border-border rounded-xl hover:bg-secondary/40 transition-colors group" data-testid={`pes-compliance-row-${row.case_id}`}>
            <Link to={row.link} className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{row.patient_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                        <span>{row.case_code} · {row.doctor_name}</span>
                        {row.sentiment && <SentimentDot value={row.sentiment} className="ml-0.5" testid={`pes-sentiment-${row.case_id}`} />}
                    </div>
                </div>
                {row.wear_time_pct_7d != null && (
                    <div className="hidden sm:block text-right shrink-0" title="7-day wear-time avg">
                        <div className="font-display text-[13px] font-bold tabular-nums leading-none">{row.wear_time_pct_7d}%</div>
                        <div className="text-[9px] text-muted-foreground uppercase tracking-wide mt-1">7d wear</div>
                    </div>
                )}
                <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold shrink-0 ${toneCls}`}>
                    {row.reason}
                </span>
            </Link>
            {/* iter98.99dx — quick check-in icon button, visible on hover. Only
                renders when thread_id exists (post-fitment cases). */}
            {row.thread_id && onCheckIn && (
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCheckIn(row); }}
                    className="shrink-0 w-7 h-7 grid place-items-center rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all opacity-70 group-hover:opacity-100"
                    title="Send 1-click check-in"
                    data-testid={`pes-checkin-btn-${row.case_id}`}
                >
                    <ChatCircleDots size={11} weight="bold" />
                </button>
            )}
        </div>
    );
}
